<!--
  @copyright 汽车商贸管理平台 V1.0
  @year 2026
  @description VehicleInventory 车辆盘点（车辆权属材料分类上传：登记证书/行驶证/检测报告/实车照片 → NAS + 订单材料附件v2）
-->
<template>
  <div class="inventory">
    <van-nav-bar title="车辆盘点" left-arrow @click-left="$router.back()" />

    <!-- 搜索栏：订单号/车架号/品牌/公司 -->
    <div class="search-bar">
      <van-search
        v-model="searchText"
        placeholder="搜索订单号/车架号/品牌/公司"
        shape="round"
      />
    </div>

    <!-- 待盘点列表：订单号 → 品牌车型×数量，展开显示车架号 -->
    <div class="order-list" v-if="!loading">
      <div v-if="orderGroups.length === 0" class="empty-tip">
        暂无待盘点任务
      </div>

      <div
        class="order-card"
        v-for="g in orderGroups"
        :key="g.orderNo"
        @click="toggleOrder(g.orderNo)"
      >
        <div class="order-head">
          <span class="order-no" :class="{ unlinked: g.orderNo === '未关联订单' }">
            {{ g.orderNo }}
          </span>
          <van-button
            v-if="g.orderNo !== '未关联订单'"
            size="mini"
            type="info"
            plain
            style="margin-left: 6px"
            @click.stop="goBatch(g.orderNo)"
            >批量盘点</van-button
          >
          <span class="order-count">共 {{ g.count }} 台</span>
          <span class="order-arrow">
            {{ expandedOrder === g.orderNo ? "收起" : "展开" }}
          </span>
        </div>

        <!-- 品牌车型 × 数量 汇总 -->
        <div class="brand-chips">
          <span class="brand-chip" v-for="(b, i) in g.brands" :key="i">
            {{ b.text }} × {{ b.count }}
          </span>
        </div>

        <!-- 展开明细：品牌车型分组 → 车架号 -->
        <div v-if="expandedOrder === g.orderNo" class="order-detail">
          <div v-for="(bg, j) in g.itemGroups" :key="j" class="brand-block">
            <div class="brand-title">{{ bg.text }} × {{ bg.count }}</div>
            <div
              class="vin-row"
              v-for="item in bg.items"
              :key="item.no"
              @click="item.orderNo ? startCheck(item) : remindUnlinked()"
            >
              <div class="vin-main">
                <div class="vin-vid">{{ item.vid }}</div>
                <div class="vin-meta">
                  {{ item.ownerCompany || "未知公司" }} ·
                  {{ item.storageLocation || "-" }}
                </div>
                <!-- 每类已传数量（点击预览；绿色=已传，灰色=未传） -->
                <div class="cat-chips" v-if="checkCategories.length > 0">
                  <span
                    class="cat-chip"
                    :class="
                      catServerCount(item, cat) > 0 ? 'done' : 'todo'
                    "
                    v-for="cat in checkCategories"
                    :key="cat.分类ID"
                    @click.stop="previewCategory(item, cat)"
                  >
                    <template v-if="catServerCount(item, cat) > 0">
                      ✓ {{ cat.分类名称 }} {{ catServerCount(item, cat) }}
                    </template>
                    <template v-else>{{ cat.分类名称 }} 未传</template>
                  </span>
                </div>
              </div>
              <div class="vin-action">
                <span v-if="!item.orderNo" class="unlinked-tip">未关联订单</span>
                <van-button
                  v-else-if="canCheck"
                  type="primary"
                  size="mini"
                  @click.stop="startCheck(item)"
                  >盘点</van-button
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 盘点弹窗：车辆权属材料分类上传 -->
    <van-dialog
      v-model="showCheckDialog"
      title="车辆盘点"
      show-cancel-button
      width="92%"
      :before-close="beforeDialogClose"
    >
      <div class="check-content">
        <div class="check-info">
          <div class="info-row">
            <span class="label">车架号：</span>
            <span>{{ currentTask.vid }}</span>
          </div>
          <div class="info-row">
            <span class="label">车辆：</span>
            <span>{{ currentTask.vehicleBand }} {{ currentTask.vehicleModel }}</span>
          </div>
          <div class="info-row">
            <span class="label">订单号：</span>
            <span>{{ currentTask.orderNo || "-" }}</span>
          </div>
          <div class="info-row">
            <span class="label">公司：</span>
            <span>{{ currentTask.ownerCompany || "未知" }}</span>
          </div>
        </div>

        <!-- 4 类车辆权属材料上传 -->
        <div class="cat-upload-list">
          <div
            class="cat-block"
            v-for="cat in checkCategories"
            :key="cat.分类ID"
          >
            <div class="cat-head">
              <span class="cat-name">{{ cat.分类名称 }}</span>
              <span class="cat-done" v-if="catDone(cat)">✓ 已传 {{ catDone(cat) }}</span>
              <span class="cat-not" v-else>未上传</span>
            </div>

            <!-- 已上传（可预览/删除） -->
            <div
              class="up-list"
              v-if="(uploadedByCat[cat.分类ID] || []).length > 0"
            >
              <div
                class="up-item"
                v-for="(u, ui) in uploadedByCat[cat.分类ID]"
                :key="u.attachmentId"
              >
                <img
                  :src="u.url"
                  class="up-thumb"
                  @click="previewUploaded(cat, ui)"
                />
                <div class="up-del" @click.stop="confirmDeleteMaterial(cat, u)">
                  ×
                </div>
              </div>
            </div>

            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              :ref="'file_' + cat.分类ID"
              style="display: none"
              @change="onCategoryPhotos(cat, $event)"
            />
            <div class="cat-actions">
              <van-button type="primary" size="mini" @click="pickCategory(cat)"
                >拍照/相册</van-button
              >
              <van-button
                v-if="(uploadedByCat[cat.分类ID] || []).length > 1"
                plain
                type="primary"
                size="mini"
                @click="previewUploaded(cat, 0)"
                >查看全部</van-button
              >
            </div>

            <!-- 待上传预览 -->
            <div class="pending-list" v-if="pendingByCat[cat.分类ID] && pendingByCat[cat.分类ID].length">
              <div
                class="pending-item"
                v-for="(p, idx) in pendingByCat[cat.分类ID]"
                :key="idx"
              >
                <img :src="p.url" class="pending-thumb" />
                <div class="pending-del" @click="removePending(cat, idx)">×</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 备注 -->
        <van-field
          v-model="checkRemark"
          type="textarea"
          placeholder="备注（选填）"
          rows="2"
          maxlength="200"
          show-word-limit
        />

        <!-- 定位信息 -->
        <div class="location-info">
          <div class="location-label">定位信息：</div>
          <div class="location-value">{{ locationName }}</div>
          <div class="location-coord" v-if="latitude">
            经纬度：{{ latitude.toFixed(6) }}, {{ longitude.toFixed(6) }}
          </div>
          <div class="location-accuracy" v-if="locationAccuracy">
            精度：{{ locationAccuracy.toFixed(0) }}米
          </div>
        </div>
      </div>
    </van-dialog>
  </div>
</template>

<script>
import {
  getPendingTasks,
  getCheckCategories,
  getVehicleMaterials,
  getMaterialFileBlob,
  uploadVehicleMaterials,
  deleteVehicleMaterial,
} from "../api/index";
import { Toast, ImagePreview, Dialog } from "vant";

export default {
  name: "VehicleInventory",
  data() {
    return {
      searchText: "",
      taskList: [],
      expandedOrder: "", // 当前展开的订单号（"" 表示全部收起）
      loading: false,
      showCheckDialog: false,
      currentTask: {},
      checkRemark: "",
      uploading: false,
      permissions: [],
      // 车辆权属下分类（登记证书/行驶证/检测报告/实车照片）
      checkCategories: [],
      // 各分类待上传照片 { 分类ID: [{file,url,name,size}] }
      pendingByCat: {},
      // 各分类已上传材料（弹窗内展示/删除） { 分类ID: [{attachmentId,fileName,url}] }
      uploadedByCat: {},
      // GPS定位信息
      latitude: null,
      longitude: null,
      locationName: "获取中...",
      locationAccuracy: null,
      // 预览用 ObjectURL（下次预览前释放）
      previewUrls: [],
    };
  },
  computed: {
    // 检查是否有盘点权限
    canCheck() {
      return this.permissions.includes("vehicle_inventory_check");
    },
    // 搜索过滤：订单号/车架号/品牌/车型/公司/存放地点 任一命中
    filteredTasks() {
      if (!this.searchText) return this.taskList;
      const keyword = this.searchText.trim().toLowerCase();
      if (!keyword) return this.taskList;
      return this.taskList.filter((item) =>
        [
          item.orderNo,
          item.vid,
          item.vehicleBand,
          item.vehicleModel,
          item.ownerCompany,
          item.storageLocation,
        ].some((f) => f && f.toLowerCase().includes(keyword))
      );
    },
    // 按订单号分组：订单 → 品牌车型×数量 → 明细车架号
    orderGroups() {
      const orders = new Map();
      this.filteredTasks.forEach((item) => {
        const orderKey = (item.orderNo || "").trim() || "未关联订单";
        if (!orders.has(orderKey)) orders.set(orderKey, []);
        orders.get(orderKey).push(item);
      });

      const result = [];
      orders.forEach((items, orderNo) => {
        const brandMap = new Map();
        items.forEach((item) => {
          const text =
            ((item.vehicleBand || "") + " " + (item.vehicleModel || "")).trim() ||
            "未知车型";
          if (!brandMap.has(text)) brandMap.set(text, { text, count: 0, items: [] });
          const bg = brandMap.get(text);
          bg.count += 1;
          bg.items.push(item);
        });
        const itemGroups = Array.from(brandMap.values());
        result.push({
          orderNo,
          count: items.length,
          brands: itemGroups.map((b) => ({ text: b.text, count: b.count })),
          itemGroups,
        });
      });

      result.sort((a, b) => {
        if (a.orderNo === "未关联订单") return 1;
        if (b.orderNo === "未关联订单") return -1;
        return b.orderNo.localeCompare(a.orderNo);
      });
      return result;
    },
  },
  created() {
    // 从 localStorage 加载权限
    const savedPerms = localStorage.getItem("userPermissions");
    if (savedPerms) {
      this.permissions = JSON.parse(savedPerms);
    }
    this.loadCheckCategories();
    this.loadTasks();
  },
  methods: {
    // 展开/收起某订单的车架号明细
    toggleOrder(orderNo) {
      this.expandedOrder = this.expandedOrder === orderNo ? "" : orderNo;
    },
    // 进入批量盘点页（按订单）
    goBatch(orderNo) {
      this.$router.push({
        path: "/batch-inventory",
        query: { orderNo: orderNo },
      });
    },
    // 未关联订单提示
    remindUnlinked() {
      Toast("该车未关联业务订单，无法上传材料，请先在电脑端关联");
    },
    async loadCheckCategories() {
      try {
        const res = await getCheckCategories();
        if (res.code === 0) {
          this.checkCategories = res.data || [];
        }
      } catch (error) {
        console.error("加载分类失败:", error);
      }
    },
    async loadTasks() {
      this.loading = true;
      this.expandedOrder = "";
      try {
        const res = await getPendingTasks();
        if (res.code === 0) {
          this.taskList = res.data || [];
        }
      } catch (error) {
        console.error("加载任务失败:", error);
      } finally {
        this.loading = false;
      }
    },
    // 获取GPS定位
    getLocation() {
      if (!navigator.geolocation) {
        this.locationName = "设备不支持定位";
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.latitude = position.coords.latitude;
          this.longitude = position.coords.longitude;
          this.locationAccuracy = position.coords.accuracy;
          this.reverseGeocode(this.latitude, this.longitude);
        },
        (error) => {
          console.error("定位失败:", error);
          this.locationName = "定位失败";
          this.latitude = null;
          this.longitude = null;
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    },
    async reverseGeocode(lat, lng) {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=zh-CN`
        );
        const data = await response.json();
        if (data && data.display_name) {
          this.locationName = data.display_name;
        } else {
          this.locationName = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        }
      } catch (error) {
        console.error("逆地理编码失败:", error);
        this.locationName = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      }
    },
    // 释放预览 URL
    releasePreviewUrls() {
      this.previewUrls.forEach((u) => URL.revokeObjectURL(u));
      this.previewUrls = [];
    },
    // 预览某车某分类已传照片（列表/弹窗通用）
    async previewCategory(item, cat) {
      if (!item || !item.orderNo) {
        Toast("未关联订单，无法查看材料");
        return;
      }
      Toast.loading({ message: "加载中...", forbidClick: true, duration: 0 });
      try {
        const res = await getVehicleMaterials({
          orderNo: item.orderNo,
          vin: item.vid,
          categoryId: cat.分类ID,
        });
        const list = (res && res.code === 0 && res.data) || [];
        if (list.length === 0) {
          Toast.clear();
          Toast("该分类还没有上传材料");
          return;
        }
        const images = [];
        this.releasePreviewUrls();
        for (const m of list) {
          const blob = await getMaterialFileBlob(m.attachmentId);
          if (blob && blob.type && blob.type.startsWith("image/")) {
            const url = URL.createObjectURL(blob);
            this.previewUrls.push(url);
            images.push(url);
          }
        }
        Toast.clear();
        if (images.length === 0) {
          Toast("没有可预览的图片");
          return;
        }
        ImagePreview({ images, startPosition: 0, closeable: true });
      } catch (error) {
        Toast.clear();
        Toast.fail("加载失败");
        console.error("预览失败:", error);
      }
    },
    // 打开盘点弹窗
    startCheck(item) {
      this.currentTask = item;
      this.checkRemark = "";
      this.pendingByCat = {};
      this.checkCategories.forEach((c) => {
        this.$set(this.pendingByCat, c.分类ID, []);
      });
      this.clearUploadedUrls();
      this.uploadedByCat = {};
      this.latitude = null;
      this.longitude = null;
      this.locationName = "获取中...";
      this.locationAccuracy = null;
      this.showCheckDialog = true;
      this.getLocation();
      this.loadUploaded();
    },
    // 释放已上传缩略图 URL
    clearUploadedUrls() {
      Object.keys(this.uploadedByCat).forEach((k) => {
        (this.uploadedByCat[k] || []).forEach((u) => URL.revokeObjectURL(u.url));
      });
    },
    // 加载该车 4 类已上传材料缩略图
    async loadUploaded() {
      const item = this.currentTask;
      if (!item || !item.orderNo || this.checkCategories.length === 0) return;
      const map = {};
      for (const cat of this.checkCategories) {
        const arr = [];
        try {
          const res = await getVehicleMaterials({
            orderNo: item.orderNo,
            vin: item.vid,
            categoryId: cat.分类ID,
          });
          const list = (res && res.code === 0 && res.data) || [];
          for (const m of list) {
            try {
              const blob = await getMaterialFileBlob(m.attachmentId);
              if (blob && blob.type && blob.type.startsWith("image/")) {
                arr.push({
                  attachmentId: m.attachmentId,
                  fileName: m.fileName || "",
                  url: URL.createObjectURL(blob),
                });
              }
            } catch (e) {
              /* 单张加载失败跳过 */
            }
          }
        } catch (e) {
          /* 忽略单分类加载失败 */
        }
        map[cat.分类ID] = arr;
      }
      this.uploadedByCat = map;
    },
    // 预览某分类已上传照片（弹窗缩略图）
    previewUploaded(cat, startIndex) {
      const arr = this.uploadedByCat[cat.分类ID] || [];
      if (arr.length === 0) {
        Toast("该分类还没有上传材料");
        return;
      }
      ImagePreview({
        images: arr.map((u) => u.url),
        startPosition: Math.min(startIndex || 0, arr.length - 1),
        closeable: true,
      });
    },
    // 删除某条已上传材料（附件行 + NAS 文件）
    confirmDeleteMaterial(cat, u) {
      Dialog.confirm({
        title: "删除材料",
        message: `确定删除该“${cat.分类名称}”照片吗？删除后不可恢复。`,
        confirmButtonColor: "#ee0a24",
      })
        .then(async () => {
          try {
            await deleteVehicleMaterial(u.attachmentId);
            URL.revokeObjectURL(u.url);
            const arr = this.uploadedByCat[cat.分类ID] || [];
            const idx = arr.findIndex((x) => x.attachmentId === u.attachmentId);
            if (idx > -1) arr.splice(idx, 1);
            this.$set(this.uploadedByCat, cat.分类ID, arr);
            // 同步当前车该分类计数（本地）
            const key = "col" + cat.分类ID;
            if (this.currentTask && this.currentTask[key] !== undefined) {
              const n = parseInt(this.currentTask[key], 10) || 0;
              this.currentTask[key] = Math.max(0, n - 1);
            }
            this.loadTasks(); // 刷新列表计数
            Toast.success("已删除");
          } catch (error) {
            Toast.fail((error && error.message) || "删除失败");
            console.error("删除材料失败:", error);
          }
        })
        .catch(() => {
          /* 取消 */
        });
    },
    // 该分类已传数量（弹窗：本地待传 + 服务端已传）
    catDone(cat) {
      const server = this.catServerCount(this.currentTask, cat);
      const pending = (this.pendingByCat[cat.分类ID] || []).length;
      return server + pending;
    },
    // 服务端已传数：col{分类ID}
    catServerCount(item, cat) {
      if (!item) return 0;
      const v = item["col" + cat.分类ID];
      const n = parseInt(v, 10);
      return isNaN(n) ? 0 : n;
    },
    pickCategory(cat) {
      const input = this.$refs["file_" + cat.分类ID];
      if (input && input[0]) input[0].click();
    },
    async onCategoryPhotos(cat, e) {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      const input = this.$refs["file_" + cat.分类ID];
      if (input && input[0]) input[0].value = "";
      Toast.loading({ message: "压缩中...", forbidClick: true, duration: 0 });
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          Toast(`${file.name} 不是图片`);
          continue;
        }
        if (file.size > 20 * 1024 * 1024) {
          Toast(`${file.name} 超过20MB限制`);
          continue;
        }
        const compressed = await this.compressImage(file);
        let finalName = file.name;
        if (
          compressed.type === "image/jpeg" &&
          !file.name.match(/\.(jpe?g)$/i)
        ) {
          finalName = file.name.replace(/\.[^.]+$/, ".jpg");
        }
        const url = URL.createObjectURL(compressed);
        const list = this.pendingByCat[cat.分类ID] || [];
        list.push({
          file: new File([compressed], finalName, { type: compressed.type }),
          url,
          name: finalName,
          size: compressed.size,
        });
        this.$set(this.pendingByCat, cat.分类ID, list);
      }
      Toast.clear();
    },
    removePending(cat, idx) {
      const list = this.pendingByCat[cat.分类ID] || [];
      if (list[idx]) URL.revokeObjectURL(list[idx].url);
      list.splice(idx, 1);
      this.$set(this.pendingByCat, cat.分类ID, list);
    },
    // 压缩照片：最大边长 1920，智能格式
    compressImage(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const maxSide = 1920;
            let { width, height } = img;
            if (width > maxSide || height > maxSide) {
              if (width > height) {
                height = Math.round((height * maxSide) / width);
                width = maxSide;
              } else {
                width = Math.round((width * maxSide) / height);
                height = maxSide;
              }
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);
            const isSmallPNG =
              file.type === "image/png" && file.size < 2 * 1024 * 1024;
            const outputType = isSmallPNG ? "image/png" : "image/jpeg";
            const quality = isSmallPNG ? undefined : 0.9;
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  resolve(blob);
                } else {
                  const dataUrl = canvas.toDataURL(outputType, quality);
                  fetch(dataUrl)
                    .then((r) => r.blob())
                    .then((b) => resolve(b))
                    .catch(() => resolve(file));
                }
              },
              outputType,
              quality
            );
          };
          img.onerror = () => resolve(file);
          img.src = e.target.result;
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
      });
    },
    // 是否有待上传照片
    hasPending() {
      return this.checkCategories.some(
        (c) => (this.pendingByCat[c.分类ID] || []).length > 0
      );
    },
    async beforeDialogClose(action, done) {
      if (action === "confirm") {
        if (this.uploading) {
          done(false);
          return;
        }
        if (!this.hasPending()) {
          // 没有新照片：4 类服务端已齐全才允许空提交完成盘点
          const allServerDone =
            this.checkCategories.length > 0 &&
            this.checkCategories.every((c) => this.catServerCount(this.currentTask, c) > 0);
          if (!allServerDone) {
            Toast("请先选择要上传的照片");
            done(false);
            return;
          }
          await this.submitMaterials();
          done(false);
          return;
        }
        await this.submitMaterials();
        done(false);
      } else {
        done();
      }
    },
    // 逐分类上传 → 服务端自动判定 4 类齐全并标记完成
    async submitMaterials() {
      this.uploading = true;
      Toast.loading({ message: "上传中...", forbidClick: true, duration: 0 });
      let lastMsg = "";
      let finished = false;
      try {
        const item = this.currentTask;
        // 有待传的分类逐个上传；无待传（服务端已齐全）则取第一个分类做空提交完成
        const catsToUpload = this.checkCategories.filter(
          (c) => (this.pendingByCat[c.分类ID] || []).length > 0
        );
        if (catsToUpload.length === 0 && this.checkCategories.length > 0) {
          catsToUpload.push(this.checkCategories[0]);
        }

        for (const cat of catsToUpload) {
          const pending = this.pendingByCat[cat.分类ID] || [];

          const formData = new FormData();
          formData.append("recordNo", item.no);
          formData.append("orderNo", item.orderNo);
          formData.append("vin", item.vid);
          formData.append("categoryId", cat.分类ID);
          formData.append("remark", this.checkRemark);
          if (this.latitude !== null) formData.append("latitude", this.latitude);
          if (this.longitude !== null) formData.append("longitude", this.longitude);
          if (this.locationName) formData.append("locationName", this.locationName);
          if (this.locationAccuracy !== null)
            formData.append("locationAccuracy", this.locationAccuracy);
          pending.forEach((p) => formData.append("photos", p.file));

          const res = await uploadVehicleMaterials(formData);
          if (res.code !== 0) {
            throw new Error(res.message || "上传失败");
          }
          if (res.data) {
            lastMsg = res.message || "";
            finished = finished || !!res.data.finished;
          }
        }

        // 释放待传 URL
        this.checkCategories.forEach((c) => {
          const list = this.pendingByCat[c.分类ID] || [];
          list.forEach((p) => URL.revokeObjectURL(p.url));
        });
        this.pendingByCat = {};

        Toast.clear();
        Toast.success(finished ? "盘点完成！" : lastMsg || "已保存");
        this.showCheckDialog = false;
        this.loadTasks(); // 刷新列表（计数/移除已完成车）
      } catch (error) {
        Toast.clear();
        Toast.fail((error && error.message) || "上传失败，请重试");
        console.error("上传失败:", error);
      } finally {
        this.uploading = false;
      }
    },
  },
  beforeDestroy() {
    this.releasePreviewUrls();
    this.clearUploadedUrls();
  },
};
</script>

<style scoped>
.inventory {
  max-width: 512px;
  margin: 0 auto;
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.search-bar {
  padding: 8px 16px;
  background: #fff;
}

.order-list {
  flex: 1;
  padding: 12px 16px;
  overflow-y: auto;
}

.order-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  cursor: pointer;
}

.order-card:active {
  background: #f8f8f8;
}

.order-head {
  display: flex;
  align-items: center;
}

.order-no {
  flex: 1;
  font-size: 15px;
  font-weight: bold;
  color: #333;
  word-break: break-all;
}

.order-no.unlinked {
  color: #999;
  font-weight: normal;
}

.order-count {
  font-size: 12px;
  color: #ff7d00;
  margin-left: 8px;
  white-space: nowrap;
}

.order-arrow {
  font-size: 12px;
  color: #999;
  margin-left: 8px;
  white-space: nowrap;
}

.brand-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.brand-chip {
  font-size: 12px;
  color: #576b95;
  background: #eef3fb;
  border-radius: 4px;
  padding: 2px 8px;
}

.order-detail {
  margin-top: 10px;
  border-top: 1px dashed #eee;
  padding-top: 6px;
}

.brand-block {
  margin-bottom: 8px;
}

.brand-title {
  font-size: 13px;
  font-weight: bold;
  color: #444;
  padding: 4px 0;
}

.vin-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fafafa;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 6px;
}

.vin-row:active {
  background: #f0f0f0;
}

.vin-main {
  flex: 1;
  min-width: 0;
}

.vin-vid {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  word-break: break-all;
}

.vin-meta {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cat-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.cat-chip {
  font-size: 11px;
  border-radius: 10px;
  padding: 0 6px;
  line-height: 16px;
}

.cat-chip.todo {
  color: #999;
  background: #f7f7f7;
  border: 1px solid #eee;
}

.cat-chip.done {
  color: #07c160;
  background: #e8f9ef;
  border: 1px solid #9fe6bf;
  font-weight: 600;
}

.vin-action {
  margin-left: 10px;
}

.unlinked-tip {
  font-size: 11px;
  color: #ff4d4f;
}

.empty-tip {
  text-align: center;
  color: #999;
  padding: 40px;
}

/* 盘点弹窗 */
.check-content {
  padding: 16px;
  max-height: 62vh;
  overflow-y: auto;
}

.check-info {
  background: #f5f5f5;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}

.info-row {
  font-size: 14px;
  color: #333;
  margin-bottom: 6px;
  word-break: break-all;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-row .label {
  color: #999;
}

.cat-upload-list {
  margin-bottom: 12px;
}

.cat-block {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
}

.cat-head {
  display: flex;
  align-items: center;
}

.cat-name {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  flex: 1;
}

.cat-done {
  font-size: 12px;
  color: #07c160;
  margin-right: 8px;
}

.cat-not {
  font-size: 12px;
  color: #ff7d00;
  margin-right: 8px;
}

.cat-actions {
  margin-top: 8px;
}

.pending-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.pending-item {
  position: relative;
  width: 64px;
  height: 64px;
}

.pending-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.pending-del {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  background: #ff4d4f;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
}

/* 已上传缩略图（可预览/删除） */
.up-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.up-item {
  position: relative;
  width: 56px;
  height: 56px;
}

.up-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.up-del {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  background: #ff4d4f;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
}

/* 定位信息样式 */
.location-info {
  background: #f0f9ff;
  border-radius: 6px;
  padding: 10px 12px;
}

.location-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.location-value {
  font-size: 14px;
  color: #333;
  word-break: break-all;
}

.location-coord,
.location-accuracy {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
</style>
